import React, { useContext, useState, useRef, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  DataSheetGrid,
  checkboxColumn,
  intColumn,
  textColumn,
  keyColumn,
} from "react-datasheet-grid";
import "react-datasheet-grid/dist/style.css";
import ConfiguratorContext from "./ConfiguratorContext";
import checkIfOptionIsVisible from "../utils/checkIfOptionIsVisible";
import checkIfBlockIsVisible from "../utils/checkIfBlockIsVisible";
import useGetOptionTitle from "../hooks/useGetOptionTitle";
import getRecursiveOutput from "../utils/getRecursiveOutput";
import ConfiguratorSurveyRenderResult from "./ConfiguratorSurveyRenderResult";
import convertRecursiveRows from "../utils/convertRecursiveRows";
import { selectColumn } from "./RecursiveQuestionTableModeSelect";
import ConfiguratorSurveyBlockRender from "./ConfiguratorSurveyBlockRender";

function formatTitle(question, intl) {
  let base = question.column_name
    ? intl.formatMessage({
        id: question.column_name,
        defaultMessage: question.column_name,
      })
    : question.title;
  const meta = question.metadata_;
  let symbol = null;
  let uom = null;
  if (meta && meta.symbol_question) {
    symbol = <sub>{meta.symbol_question}</sub>;
  }
  if (meta && meta.uom_question) {
    uom = <b>[{meta.uom_question}]</b>;
  }
  return (
    <>
      {base}{symbol} {uom}
    </>
  );
}

const ConfiguratorSurveyRecursiveQuestionTableMode = ({
  id,
  blocks,
  onChange,
  responsesToCheck,
  onChangeRecursive,
  list,
  role,
  user,
  currentRecursionResponses,
  setCurrentRecursionResponses,
  editable,
  realtime,
  summary
}) => {
  const context = useContext(ConfiguratorContext);
  const { configuration, responses } = context;
  const intl = useIntl();
  const getOptionTitle = useGetOptionTitle();
  const recursiveOutputs = useRef(
    blocks.filter((b) => b.type === "output" && b.output_type === "calculation")
  );
  const lastOutput = useRef(
    recursiveOutputs.current.length
      ? recursiveOutputs.current[recursiveOutputs.current.length - 1]
      : null
  );
  const listRef = useRef(list);
  listRef.current = list;

  const rows = convertRecursiveRows(list, blocks, getOptionTitle, context, id);
  const mt = realtime ? "mt-4" : "mt-6";

  const isKCCOptimization =
    lastOutput.current?.calculation?.length === 1 &&
    lastOutput.current.calculation[0].startsWith("{") &&
    JSON.parse(lastOutput.current.calculation[0]).original ===
      "kcc_optimization";

  // TODO in case of KCC optimization the total cannot be computed directly: we should retrieve the single configs
  //  totals from the kcc_optimization_results_table, and we should handle the case in which the totals haven't been
  //  already computed (via KK external requests). For now we're just going to hide the total in this particular case
  const total = !isKCCOptimization
    ? getRecursiveOutput(lastOutput.current, rows)
    : null;

  const getEditComponent = (question) => {
    let activeResponses = currentRecursionResponses || responses;
    let value = activeResponses[question.id]?.value;

    const { options, input_type } = question;
    switch (input_type) {
      case "bool":
        return checkboxColumn;
      case "text_input":
        return textColumn;
      case "number_input":
        return intColumn;

      case "multi_select":
      case "select":
        const selectOptions = options
          .filter((o) => {
            return checkIfOptionIsVisible(
              o,
              {
                ...context,
                responses: responsesToCheck,
              },
              user,
              value
            );
          })
          .map((option) => ({
            value: `${option.id}`,
            label: option.title,
          }));
        return selectColumn({
          choices: selectOptions,
          isMulti: input_type === "multi_select",
        });

      default:
        throw new Error(
          `Unknown input type ${input_type} for question ${question.id}`
        );
    }
  };
  const question2Column = (question) => {
    return {
      ...keyColumn(`${question.id}`, getEditComponent(question)),
      minWidth: question.input_type === "select" ? 200 : 100,
      disabled: ({ rowData, rowIndex, columnId }) => {
        if (!editable) return true;
        const responses = listRef.current[rowIndex] || {};

        const isdisabled = !checkIfBlockIsVisible(question, {
          ...context,
          responses: responses,
          configuration,
        });

        return isdisabled;
      },

      title: formatTitle(question, intl),
    };
  };

  const activeColumns = blocks
    .filter((block) => block.type === "question" && block.active)
    .map((question) => question2Column(question));

  const recursionResponsesToData = (recursionResponses, i) => {
    let rowWithValues = {};
    for (let k of Object.keys(recursionResponses)) {
      if (recursionResponses[k]) {
        rowWithValues[k] = recursionResponses[k].value;
      }
    }
    return rowWithValues;
  };

    const getResponsesToCheck = (current) => {
    if (Object.keys(context?.responses || {}).length === 0)
      return {};
    // substitute to the current recursive question responses the current row only, in order to let the dependencies
    // check work properly
    return {
      // 1. keep all survey responses, since they might be needed in any block inside this recursive question
      ...context.responses,
      // 2. keep the current recursive question, but keep the current row/recursion only
      [id]: {
        ...context.responses[id],
        recursive_question_value: [current]
      },
      // 3. spread the current recursion responses, since they can be used directly by the blocks inside this recursive
      //    question
      ...current,
    };
  };

  return (
    <div className="my-10">
      <DataSheetGrid
        value={list.map((recResp, i) => recursionResponsesToData(recResp, i))}
        onChange={(newData, operations) => {
          if (!editable) return;
          let listClone = _.clone(list);
          for (let operation of operations) {
            if (operation.type === "DELETE") {
              const fromIndex = operation.fromRowIndex;
              const toIndex = operation.toRowIndex;
              listClone = listClone
                .slice(0, fromIndex)
                .concat(listClone.slice(toIndex));
              onChange(listClone);
            } else if (
              operation.type === "UPDATE" ||
              operation.type === "CREATE"
            ) {
              let newState = {};
              const fromIndex = operation.fromRowIndex;
              const toIndex = operation.toRowIndex;
              const updateState = (newData, index) => {
                Object.entries(newData[index]).forEach(([k, v]) => {
                  let q = blocks.find((b) => b.id === parseInt(k));
                  const isOption = [
                    "select",
                    "multi_select",
                    "kcc_color_input",
                  ].includes(q.input_type);
                  newState = {
                    ...newState,
                    ...onChangeRecursive(v, isOption, q, false),
                  };
                });
              };

              for (let i = fromIndex; i < toIndex; i++) {
                updateState(newData, i);
                delete listClone[i];
                listClone[i] = newState;
              }
              onChange(listClone);
            }
          }
        }}
        lockRows={!editable}
        columns={activeColumns}
      />
      {!editable && <div className="flex w-full flex-col">
        {list.map((recursiveResponses, index) => (
          <div
            key={index}
            className={
              index === list.length - 1
                ? "w-full flex flex-col py-1"
                : "w-full flex flex-col border-b-2 border-black py-1"
            }
          >
            {blocks
              .filter(
                (b) =>
                  b.type === "output" &&
                  checkIfBlockIsVisible(b, {
                    ...context,
                    responses: getResponsesToCheck(recursiveResponses),
                  }, index)
              )
              .map((output) => (
                <ConfiguratorSurveyBlockRender
                  key={output.id}
                  recursiveResponses={getResponsesToCheck(recursiveResponses)}
                  recursiveResponsesIndex={index}
                  block={output}
                  role={role}
                  summary={summary}
                />
              ))}
          </div>
        ))}
      </div>}
      {total ? (
        <div className={`flex w-full justify-between ${mt}`}>
          <h2 className="text-2xl">
            <FormattedMessage id="total" defaultMessage="Total" />
          </h2>
          <h2 className="text-2xl">
            <ConfiguratorSurveyRenderResult
              result={total}
              currency={lastOutput.current.output_currency}
            />
          </h2>
        </div>
      ) : null}
    </div>
  );
};

export default ConfiguratorSurveyRecursiveQuestionTableMode;
